import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import EastIcon from '@mui/icons-material/East';

import { TOKENS } from '../theme';
import {
  actionMeta,
  entityMeta,
  entityIdentifier,
  changeRows,
  formatChangedAt,
} from './auditFormat';

export default function ManagerModificationsList({
  auditLog = [],
  customers = [],
  employees = [],
  manageBonusList = [],
}) {
  const lookups = { customers, employees, manageBonusList };

  // Only show actual modifications — approve/flag decisions are tracked
  // separately in the bonus/raffle review surfaces, not here.
  const visible = auditLog.filter((e) =>
    ['CREATE', 'EDIT', 'DELETE'].includes(e?.action)
  );
  if (!visible.length) return null;

  const sorted = [...visible].sort((a, b) => {
    const ta = new Date(a.changedAt || 0).getTime();
    const tb = new Date(b.changedAt || 0).getTime();
    return tb - ta;
  });

  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.slate50,
          borderBottom: `1px solid ${TOKENS.slate200}`,
          px: 1.5,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate700,
          }}
        >
          Manager Modifications
        </Typography>
        <Chip
          size="small"
          label={`${sorted.length} ${
            sorted.length === 1 ? 'change' : 'changes'
          }`}
          sx={{
            height: 18,
            fontSize: 10,
            background: TOKENS.slate100,
            color: TOKENS.slate700,
          }}
        />
      </Box>
      <Box>
        {sorted.map((entry, i) => (
          <AuditRow
            key={entry.auditId || i}
            entry={entry}
            lookups={lookups}
            isLast={i === sorted.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
}

function AuditRow({ entry, lookups, isLast }) {
  const action = actionMeta(entry);
  const ent = entityMeta(entry.entityType);
  const ident = entityIdentifier(entry, lookups);
  const rows = changeRows(entry, lookups);
  const time = formatChangedAt(entry.changedAt);
  const who = entry.changedByName || 'Manager';

  return (
    <Box
      // data-pdf-block — the PDF generator paginates at these boundaries so
      // an individual audit entry never splits across two pages.
      data-pdf-block="audit-row"
      sx={{
        display: 'flex',
        gap: 1,
        px: 1.5,
        py: 1.25,
        borderBottom: isLast ? 'none' : `1px solid ${TOKENS.slate100}`,
      }}
    >
      <Box
        sx={{
          width: 70,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
        }}
      >
        <Chip
          size="small"
          label={action.label}
          sx={{
            height: 18,
            fontSize: 9,
            fontWeight: 700,
            background: action.bg,
            color: action.color,
            letterSpacing: '0.05em',
          }}
        />
        {time && (
          <Typography sx={{ fontSize: 10, color: TOKENS.slate500 }}>
            {time}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
            mb: rows.length ? 0.5 : 0,
          }}
        >
          <Chip
            size="small"
            label={ent.label}
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              background: ent.bg,
              color: ent.color,
            }}
          />
          {ident && (
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.slate900 }}
            >
              {ident}
            </Typography>
          )}
        </Box>
        {rows.length > 0 && entry.action === 'EDIT' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {rows.map((r) => (
              <ChangeLine key={r.field} {...r} />
            ))}
          </Box>
        )}
        <Typography
          sx={{ fontSize: 11, color: TOKENS.slate500, mt: 0.5 }}
          noWrap
        >
          by{' '}
          <span style={{ fontWeight: 600, color: TOKENS.slate700 }}>{who}</span>
        </Typography>
      </Box>
    </Box>
  );
}

function ChangeLine({ label, oldVal, newVal }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
          minWidth: 70,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: TOKENS.slate500,
          fontFamily: 'monospace',
          textDecoration: 'line-through',
        }}
      >
        {oldVal}
      </Typography>
      <EastIcon sx={{ fontSize: 11, color: TOKENS.slate400 }} />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: TOKENS.slate900,
          fontFamily: 'monospace',
        }}
      >
        {newVal}
      </Typography>
    </Box>
  );
}
